import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import DataTable, { Column } from '@repo/ui/data-table'
import { CheckMarkIcon, DeleteIcon, MessageIcon, PenIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createCenterLecComment } from '../../../../../../../api/centers/center-lec-comments/create-center-lec-comment'
import { deleteCenterLecComments } from '../../../../../../../api/centers/center-lec-comments/delete-center-lec-comment'
import { getCenterLecComments } from '../../../../../../../api/centers/center-lec-comments/get-center-lec-comments'
import { updateCenterLecComment } from '../../../../../../../api/centers/center-lec-comments/update-center-lec-comment'
import { useModalManager } from '../../../../../../../lib/hooks/use-modal-manager'
import { CenterLecComment } from '../../../../../../../lib/types/models/center-lec-comment/center-lec-comment'
import { modalButtonConfig } from '../../../../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../../../../../main'
import ActionTableWrapper from '../../../../../../elements/action-table-wrapper'
import CenterLecCommentForm from './center-lec-comment-form'
import { mapCenterLecCommentToInput } from './utils'

type Props = {
  id: number
  bodyColorVariant?: 'white' | 'grey'
}

const CenterLecCommentTable = ({ id, bodyColorVariant = 'grey' }: Props) => {
  const { modalState, closeModel, selectedData, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    CenterLecComment
  >(['create', 'edit', 'delete'])

  const { data: centerLecComments } = useQuery({
    queryKey: ['host-family-lec-comments'],
    queryFn: () => getCenterLecComments({ centerId: id })
  })

  const { mutateAsync: deleteComment, isPending: isLoading } = useMutation({
    mutationFn: deleteCenterLecComments,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Commentaire supprimé avec succès')
      queryClient.invalidateQueries({
        queryKey: ['host-family-lec-comments']
      })
      closeModel('delete')
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: createCenterLecComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Commentaire ajouté avec succès')
      queryClient.invalidateQueries({
        queryKey: ['host-family-lec-comments']
      })
      closeModel('create')
    }
  })

  const { mutateAsync: updateComment } = useMutation({
    mutationFn: updateCenterLecComment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Commentaire modifié avec succès')
      queryClient.invalidateQueries({
        queryKey: ['host-family-lec-comments']
      })
      closeModel('edit')
    }
  })

  const columns: Column<CenterLecComment>[] = [
    {
      enableSorting: true,
      header: 'Commentaire',
      accessorKey: 'comment',
      cell: ({ row }) => (
        <Typography
          variant="tag"
          color={tailwindColors.black.DEFAULT}
          className="text-wrap px-3 py-4"
        >
          {row.original.comment}
        </Typography>
      )
    },
    {
      enableSorting: true,
      header: 'Commenté par',
      accessorKey: 'commentedBy',
      cell: ({ row }) => (
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {toFullName(row.original.commentedBy.firstName, row.original.commentedBy.lastName)}
        </Typography>
      )
    },

    {
      enableSorting: true,
      header: 'Date & heure',
      accessorKey: 'createdAt',
      cell: ({ row }) => (
        <Badge
          text={`${DateFormatter.DDMMYYYY(row.original.updatedAt)} - ${DateFormatter.HHmm(row.original.updatedAt)}`}
          variant="grey"
        />
      )
    },
    {
      enableSorting: true,
      header: 'Partagé à l’OL ?',
      accessorKey: 'sharedToOl',
      centerCell: true,
      cell: ({ row }) =>
        row.original.sharedToOl ? (
          <div className="w-fit rounded-full bg-green-light p-2">
            <CheckMarkIcon color={tailwindColors.green.DEFAULT} width="16px" height="16px" />
          </div>
        ) : (
          '-'
        )
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => {
              toggleModal('edit', row.original)
            }}
          />
          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => {
              toggleModal('delete', row.original)
            }}
          />
        </div>
      )
    }
  ]

  return (
    <Box
      className={cn('bg-white-hover rounded-lg border border-grey-light p-0', {
        'bg-white': bodyColorVariant === 'white',
        'bg-white-hover': bodyColorVariant === 'grey'
      })}
    >
      <ActionTableWrapper
        title={'Commentaires LEC'}
        transparent
        actions={[
          <Button
            key={'add'}
            type="button"
            icon={PlusIcon}
            label={'Ajouter un commentaire'}
            variant={'outline'}
            onClick={() => toggleModal('create')}
          />
        ]}
      >
        <DataTable<CenterLecComment>
          manualSorting={false}
          bodyVariant="white"
          columns={columns}
          data={centerLecComments?.data ?? []}
          rowCount={0}
          paginated={false}
        />

        <Modal
          isOpen={modalState.create}
          title={'Écrire un commentaire'}
          size="medium"
          hideActions
          icon={MessageIcon}
          onCancel={() => closeModel('create')}
        >
          <CenterLecCommentForm
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              onConfirm: async (values) => {
                await mutateAsync({ ...values, centerId: id })
              }
            })}
            defaultValues={{
              sharedToOl: false
            }}
          />
        </Modal>

        {selectedData && (
          <Modal
            isOpen={modalState.edit}
            title={'Modifier un commentaire'}
            hideActions
            icon={MessageIcon}
            size="medium"
            onCancel={() => closeModel('edit')}
          >
            <CenterLecCommentForm
              defaultValues={mapCenterLecCommentToInput(selectedData)}
              buttonsConfig={modalButtonConfig({
                onCancel: () => closeModel('edit'),
                onConfirm: async (values) => {
                  await updateComment({
                    ...values,
                    centerId: id,
                    id: selectedData.id
                  })
                }
              })}
            />
          </Modal>
        )}

        {selectedData && (
          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: async () => {
                await deleteComment({ centerId: id, ids: [selectedData.id] })
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Supprimer un commentaire'}
            icon={DeleteIcon}
            isLoading={isLoading}
            variant={'destructive'}
          />
        )}
      </ActionTableWrapper>
    </Box>
  )
}

export default CenterLecCommentTable
